"use client";

import * as React from "react";
import { cn } from "@/lib/utils";
import { LuX } from "react-icons/lu";

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

interface TagInputContextValue {
  tags: string[];
  addTag: (tag: string) => void;
  removeTagAt: (index: number) => void;
  activeTagIndex: number | null;
  setActiveTagIndex: React.Dispatch<React.SetStateAction<number | null>>;
  inputRef: React.RefObject<HTMLInputElement | null>;
  disabled: boolean;
}

const TagInputContext = React.createContext<TagInputContextValue | null>(null);

function useTagInput() {
  const context = React.use(TagInputContext);
  if (!context) {
    throw new Error("useTagInput must be used within a <TagInput>");
  }
  return context;
}

/** Per‑tag context so TagInputTagRemove knows which tag it belongs to. */
interface TagMeta {
  value: string;
  index: number;
}

const TagInputTagContext = React.createContext<TagMeta | null>(null);

// ---------------------------------------------------------------------------
// TagInput — Root provider
// ---------------------------------------------------------------------------

interface TagInputProps {
  /** Controlled tag list. */
  value?: string[];
  /** Initial tags for uncontrolled usage. */
  defaultValue?: string[];
  /** Callback fired when the tag list changes. */
  onValueChange?: (tags: string[]) => void;
  /** Maximum number of tags allowed. */
  maxTags?: number;
  /** Allow duplicate tag values. @default false */
  allowDuplicates?: boolean;
  /** Disable the entire tag input. */
  disabled?: boolean;
  className?: string;
  children: React.ReactNode;
}

function TagInput({
  value: controlledValue,
  defaultValue = [],
  onValueChange,
  children,
  maxTags,
  allowDuplicates = false,
  disabled = false,
  className,
}: TagInputProps) {
  const [uncontrolledValue, setUncontrolledValue] =
    React.useState(defaultValue);
  const [activeTagIndex, setActiveTagIndex] = React.useState<number | null>(
    null,
  );
  const inputRef = React.useRef<HTMLInputElement | null>(null);

  const isControlled = controlledValue !== undefined;
  const tags = isControlled ? controlledValue : uncontrolledValue;

  const updateTags = React.useCallback(
    (next: string[]) => {
      if (!isControlled) setUncontrolledValue(next);
      onValueChange?.(next);
    },
    [isControlled, onValueChange],
  );

  const addTag = React.useCallback(
    (tag: string) => {
      const trimmed = tag.trim();
      if (!trimmed) return;
      if (!allowDuplicates && tags.includes(trimmed)) return;
      if (maxTags !== undefined && tags.length >= maxTags) return;
      updateTags([...tags, trimmed]);
    },
    [tags, allowDuplicates, maxTags, updateTags],
  );

  const removeTagAt = React.useCallback(
    (index: number) => {
      updateTags(tags.filter((_, i) => i !== index));
      setActiveTagIndex(null);
    },
    [tags, updateTags],
  );

  const ctx = React.useMemo<TagInputContextValue>(
    () => ({
      tags,
      addTag,
      removeTagAt,
      activeTagIndex,
      setActiveTagIndex,
      inputRef,
      disabled,
    }),
    [tags, addTag, removeTagAt, activeTagIndex, disabled],
  );

  return (
    <TagInputContext value={ctx}>
      <div
        data-slot="tag-input"
        data-disabled={disabled || undefined}
        className={className}
      >
        {children}
      </div>
    </TagInputContext>
  );
}

// ---------------------------------------------------------------------------
// TagInputGroup — styled container that auto‑renders tags + children (input)
// ---------------------------------------------------------------------------

function TagInputGroup({
  className,
  children,
  ...props
}: React.ComponentProps<"div">) {
  const { tags, inputRef, disabled } = useTagInput();

  return (
    <div
      data-slot="tag-input-group"
      data-disabled={disabled || undefined}
      className={cn(
        // Borrowed from Input outer <span> so TagInput blends with other inputs
        "relative w-full rounded-lg border border-input bg-background not-dark:bg-clip-padding text-base text-foreground shadow-xs/5 ring-primary/24 transition-shadow before:pointer-events-none before:absolute before:inset-0 before:rounded-[calc(var(--radius-lg)-1px)] not-has-disabled:not-has-focus-visible:not-has-aria-invalid:before:shadow-[0_1px_--theme(--color-black/4%)] has-focus-visible:has-aria-invalid:border-destructive/64 has-focus-visible:has-aria-invalid:ring-destructive/16 has-aria-invalid:border-destructive/36 has-focus-visible:border-primary has-disabled:opacity-64 has-[:disabled,:focus-visible,[aria-invalid]]:shadow-none has-focus-visible:ring-[3px] sm:text-sm dark:bg-input/32 dark:has-aria-invalid:ring-destructive/24 dark:not-has-disabled:not-has-focus-visible:not-has-aria-invalid:before:shadow-[0_-1px_--theme(--color-white/6%)]",
        // Tag-specific layout — py-0 when empty so height matches Input exactly,
        // py-1 when tags are present to give them breathing room.
        "flex flex-wrap items-center gap-1 px-1 has-data-[slot=tag-input-tag]:py-1",
        className,
      )}
      onClick={() => {
        if (!disabled) inputRef.current?.focus();
      }}
      {...props}
    >
      {tags.map((tag, index) => (
        <TagInputTag key={`${tag}-${index}`} value={tag} index={index}>
          {tag}
          <TagInputTagRemove />
        </TagInputTag>
      ))}
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// TagInputTag — individual tag pill
// ---------------------------------------------------------------------------

interface TagInputTagProps extends React.ComponentProps<"span"> {
  /** Tag string value. */
  value: string;
  /**
   * Index of this tag in the tags array. When omitted the index is derived via
   * `tags.indexOf(value)` which works correctly when duplicates are disabled.
   */
  index?: number;
}

function TagInputTag({
  className,
  value,
  index: indexProp,
  children,
  ...props
}: TagInputTagProps) {
  const { activeTagIndex, tags } = useTagInput();
  const index = indexProp ?? tags.indexOf(value);
  const isActive = activeTagIndex === index;

  const meta = React.useMemo<TagMeta>(
    () => ({ value, index }),
    [value, index],
  );

  return (
    <TagInputTagContext value={meta}>
      <span
        data-slot="tag-input-tag"
        data-active={isActive || undefined}
        className={cn(
          "bg-background border-input hover:bg-background relative inline-flex h-7 items-center gap-1 rounded-md border pe-7 ps-2 text-xs font-medium transition-colors data-active:ring-ring/50 data-active:ring-2",
          className,
        )}
        {...props}
      >
        {children}
      </span>
    </TagInputTagContext>
  );
}

// ---------------------------------------------------------------------------
// TagInputTagRemove — remove button rendered inside a TagInputTag
// ---------------------------------------------------------------------------

function TagInputTagRemove({
  className,
  ...props
}: Omit<React.ComponentProps<"button">, "type" | "onClick" | "children">) {
  const meta = React.use(TagInputTagContext);
  const { removeTagAt, inputRef, disabled } = useTagInput();

  if (!meta) return null;

  return (
    <button
      type="button"
      data-slot="tag-input-tag-remove"
      aria-label={`Remove ${meta.value}`}
      disabled={disabled}
      className={cn(
        "absolute -inset-y-px -end-px flex size-7 items-center justify-center rounded-e-md p-0 text-muted-foreground/80 outline-none transition-[color,box-shadow] hover:text-foreground focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]",
        className,
      )}
      onClick={(e) => {
        e.stopPropagation();
        removeTagAt(meta.index);
        inputRef.current?.focus();
      }}
      {...props}
    >
      <LuX className="size-3.5" />
    </button>
  );
}

// ---------------------------------------------------------------------------
// TagInputField — text input for entering new tags
// ---------------------------------------------------------------------------

function TagInputField({
  className,
  onKeyDown,
  ...props
}: React.ComponentProps<"input">) {
  const {
    addTag,
    removeTagAt,
    tags,
    activeTagIndex,
    setActiveTagIndex,
    inputRef,
    disabled,
  } = useTagInput();

  function handleKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    const value = inputRef.current?.value ?? "";

    if (event.key === "Enter") {
      event.preventDefault();
      if (value) {
        addTag(value);
        if (inputRef.current) inputRef.current.value = "";
      }
    }

    // Backspace on empty input: first press highlights last tag, second removes it
    if (event.key === "Backspace" && !value && tags.length > 0) {
      if (activeTagIndex !== null) {
        removeTagAt(activeTagIndex);
      } else {
        setActiveTagIndex(tags.length - 1);
      }
    }

    // Arrow key navigation between tags when input is empty
    if (event.key === "ArrowLeft" && !value && tags.length > 0) {
      event.preventDefault();
      setActiveTagIndex((prev) =>
        prev === null ? tags.length - 1 : Math.max(0, prev - 1),
      );
    }

    if (event.key === "ArrowRight" && !value && activeTagIndex !== null) {
      event.preventDefault();
      setActiveTagIndex((prev) =>
        prev !== null && prev >= tags.length - 1 ? null : (prev ?? -1) + 1,
      );
    }

    if (event.key === "Escape") {
      setActiveTagIndex(null);
      inputRef.current?.blur();
    }

    onKeyDown?.(event);
  }

  return (
    <input
      ref={inputRef}
      data-slot="tag-input-field"
      disabled={disabled}
      className={cn(
        // Height/leading from Input default size; px accounts for group's px-1
        "h-8.5 min-w-[80px] flex-1 bg-transparent px-[calc(--spacing(2)-1px)] leading-8.5 outline-none placeholder:text-muted-foreground/72 sm:h-7.5 sm:leading-7.5",
        className,
      )}
      onKeyDown={handleKeyDown}
      onChange={() => setActiveTagIndex(null)}
      {...props}
    />
  );
}

// ---------------------------------------------------------------------------
// Exports
// ---------------------------------------------------------------------------

export {
  TagInput,
  TagInputGroup,
  TagInputTag,
  TagInputTagRemove,
  TagInputField,
  useTagInput,
};
